import { useEffect, useState } from 'react';
import { Banner } from '../design/primitives/Misc';
import { Button } from '../design/primitives/Button';
import { fetchServedBuild, type ServedBuild } from '../api/version';
import { BUILD } from '../../lib/build';

// "A newer build is on the server." The bundle knows the commit it was built from; /env.js
// (no-store) says which commit the server currently serves. When both are known and differ, the
// tab is stale — offer a reload instead of letting it talk to an API it was not built against.
// Checked on mount, whenever the tab regains focus/visibility, and on a slow interval.

const DEFAULT_INTERVAL_MS = 5 * 60_000;

export interface StaleBuildOptions {
  /** The sha this bundle was built from (default: BUILD.sha). 'unknown' disables the check. */
  currentSha?: string;
  /** Reads the served build (default: GET /env.js). */
  fetchServed?: () => Promise<ServedBuild | null>;
  intervalMs?: number;
}

export function useStaleBuild(opts: StaleBuildOptions = {}): { stale: boolean; servedSha: string | null } {
  const currentSha = opts.currentSha ?? BUILD.sha;
  const fetchServed = opts.fetchServed ?? fetchServedBuild;
  const intervalMs = opts.intervalMs ?? DEFAULT_INTERVAL_MS;
  const [servedSha, setServedSha] = useState<string | null>(null);

  useEffect(() => {
    if (!currentSha || currentSha === 'unknown') return undefined;
    let live = true;
    const check = async () => {
      const served = await fetchServed();
      if (live && served && served.sha !== 'unknown') setServedSha(served.sha);
    };
    const onVisible = () => {
      if (document.visibilityState === 'visible') void check();
    };
    void check();
    window.addEventListener('focus', onVisible);
    document.addEventListener('visibilitychange', onVisible);
    const timer = window.setInterval(() => void check(), intervalMs);
    return () => {
      live = false;
      window.removeEventListener('focus', onVisible);
      document.removeEventListener('visibilitychange', onVisible);
      window.clearInterval(timer);
    };
  }, [currentSha, fetchServed, intervalMs]);

  return { stale: servedSha !== null && servedSha !== currentSha, servedSha };
}

export function StaleBuildBanner(opts: StaleBuildOptions = {}) {
  const { stale } = useStaleBuild(opts);
  if (!stale) return null;
  return (
    <Banner
      tone="warn"
      icon="refresh-cw"
      action={
        <Button size="sm" onClick={() => window.location.reload()}>
          Reload
        </Button>
      }
    >
      A newer version of NovaPSA has been deployed. Reload to pick it up.
    </Banner>
  );
}
